/**
 * Audit Documents Data Hook
 *
 * Fetches documents linked to an audit (directly or via documentChecklistLinks)
 * and maps them to DocumentView[] for the UI layer.
 */

import { api } from "convex/_generated/api";
import { useMemo } from "react";
import { toDocumentView } from "@/packages/types";
import type { AuditId, DocumentView } from "@/packages/types";
import { useWorkspaceQuery } from "@/hooks";

/* ------------------------------------------------------------------ */
/* useAuditDocuments                                                    */
/* ------------------------------------------------------------------ */

interface UseAuditDocumentsReturn {
  documents: DocumentView[];
  groupedDocuments: Map<string, DocumentView[]>;
  isLoading: boolean;
}

export function useAuditDocuments(
  auditId: AuditId | null,
): UseAuditDocumentsReturn {
  const { data, isLoading } = useWorkspaceQuery(
    api.document_queries.listDocumentsByAudit,
    { auditId: auditId ?? null },
  );

  const documents = useMemo<DocumentView[]>(() => {
    if (!data) return [];
    return data.map(toDocumentView);
  }, [data]);

  const groupedDocuments = useMemo(() => {
    const groups = new Map<string, DocumentView[]>();
    for (const doc of documents) {
      const key = doc.categoryId ?? "uncategorized";
      const existing = groups.get(key) ?? [];
      existing.push(doc);
      groups.set(key, existing);
    }
    return groups;
  }, [documents]);

  return { documents, groupedDocuments, isLoading };
}
